/**
 * TR-70 — the HSA itself, on the Trends page: what it holds, and how much of
 * that is what went in and how much is growth.
 *
 * The custodian has no API, so the figures are copied in from its website and
 * dated. Three numbers are entered; growth is worked out. After three months
 * the card says the figures are old and asks for new ones, and Home lists it
 * as a reminder.
 *
 * Every reading is kept, and from the second one on the card draws them as a
 * line over time: what went in at the bottom, the growth on top.
 */
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '../api.js';
import { claims } from '../auth.js';
import { useApi, useAction } from '../hooks.js';
import { ErrorBox, Notice, centsToInput, formatDate, formatMoney, inputToCents, toast } from '../ui.js';
import { BalanceChart } from '../charts.js';
import { balanceSeries, daysSince, isStale, latestBalance, splitBalance } from '../trends.js';
import type { AppConfigResponse, HsaBalance } from '../types.js';

/** Today on the viewer's calendar, as `YYYY-MM-DD`. */
function localToday(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const percent = (rate: number): string => `${(rate * 100).toFixed(1)}%`;
const named = (source: string): string => source.charAt(0).toUpperCase() + source.slice(1);

export function HsaCard({ ready }: { ready: number }) {
  const config = useApi<AppConfigResponse>('/api/config');
  const isAdmin = (claims()?.['cognito:groups'] ?? []).includes('admin');
  const [editing, setEditing] = useState(false);
  const { hash } = useLocation();
  const latest = latestBalance(config.data?.config.hsa_balances);

  // Home's reminder links straight here.
  useEffect(() => {
    if (hash === '#hsa' && config.data) document.getElementById('hsa')?.scrollIntoView({ block: 'start' });
  }, [hash, config.data]);

  return (
    <section className="card" id="hsa">
      <div className="card-head">
        <div>
          <h2>Your HSA</h2>
          <div className="sub">
            {latest
              ? `${named(latest.source)}, as of ${formatDate(latest.as_of, { year: 'always' })}`
              : 'What the account holds, and how much of it is growth'}
          </div>
        </div>
        {isAdmin && !editing && config.data ? (
          <div className="picks">
            <button className={latest ? 'quiet' : 'primary'} onClick={() => setEditing(true)}>
              {latest ? 'Update' : 'Add the balance'}
            </button>
          </div>
        ) : null}
      </div>

      {config.error ? <ErrorBox error={config.error} /> : null}
      {editing ? (
        <BalanceForm
          latest={latest}
          custodian={config.data?.config.custodian ?? ''}
          onCancel={() => setEditing(false)}
          onSaved={() => {
            setEditing(false);
            config.reload();
          }}
        />
      ) : latest ? (
        <Figures
          latest={latest}
          readings={config.data?.config.hsa_balances ?? []}
          ready={ready}
          isAdmin={isAdmin}
          onChange={config.reload}
        />
      ) : config.data ? (
        <p className="small muted">
          {isAdmin
            ? 'Copy in the balance from your HSA’s website to see how much of it is what you put in and how much is growth.'
            : 'The account owner can add the balance from the HSA’s website.'}
        </p>
      ) : null}
    </section>
  );
}

function Figures({
  latest,
  readings,
  ready,
  isAdmin,
  onChange,
}: {
  latest: HsaBalance;
  readings: HsaBalance[];
  ready: number;
  isAdmin: boolean;
  onChange: () => void;
}) {
  const s = splitBalance(latest);
  const points = balanceSeries(readings);
  const days = daysSince(latest.as_of, new Date());
  return (
    <div className="stack tight">
      {isStale(latest, new Date()) ? (
        <Notice kind="warn" title="Time to update the HSA balance">
          These figures are from {formatDate(latest.as_of, { year: 'always' })}, {days} days ago.{' '}
          {isAdmin ? 'Press Update and copy the new ones from the HSA’s website.' : 'The account owner can update them.'}
        </Notice>
      ) : null}

      <div className="hsa-total">{formatMoney(s.balance)}</div>
      <div
        className="hsa-bar"
        role="img"
        aria-label={`${formatMoney(s.kept)} of it contributed, ${formatMoney(s.growth)} growth`}
      >
        {s.keptShare > 0 ? <span style={{ width: `${s.keptShare * 100}%`, background: 'var(--s1)' }} /> : null}
        {s.growthShare > 0 ? <span style={{ width: `${s.growthShare * 100}%`, background: 'var(--s5)' }} /> : null}
      </div>

      <div className="hsa-rows">
        <div className="hsa-row">
          <i className="swatch" style={{ background: 'var(--s1)' }} />
          <span className="name">Contributed</span>
          <span className="value">{formatMoney(latest.contributed_cents)}</span>
          <span className="note">Yours and your employer’s, every year.</span>
        </div>
        {latest.withdrawn_cents > 0 ? (
          <div className="hsa-row">
            <i className="swatch" />
            <span className="name">Taken out</span>
            <span className="value">−{formatMoney(latest.withdrawn_cents)}</span>
          </div>
        ) : null}
        <div className="hsa-row">
          <i className="swatch" style={{ background: 'var(--s5)' }} />
          <span className="name">Growth</span>
          <span className="value">{formatMoney(s.growth)}</span>
          {s.growthRate !== null ? (
            <span className="note">
              {s.growth >= 0 ? 'Up' : 'Down'} {percent(Math.abs(s.growthRate))} on what was contributed.
            </span>
          ) : null}
        </div>
      </div>

      {points.length > 1 ? (
        <BalanceChart
          points={points}
          label={`The HSA's balance at each of ${points.length} readings, from ${formatDate(points[0]!.as_of, { year: 'always' })} to ${formatDate(latest.as_of, { year: 'always' })}`}
        />
      ) : (
        <p className="xs muted">Each update adds a point. From the second, this shows the balance over time.</p>
      )}

      {ready > 0 ? (
        <p className="xs muted">
          {ready <= s.balance
            ? `${formatMoney(ready)} of it is ready to claim for expenses already filed.`
            : `${formatMoney(ready)} is ready to claim for expenses already filed, more than the balance.`}
        </p>
      ) : null}

      <Readings readings={readings} isAdmin={isAdmin} onChange={onChange} />
    </div>
  );
}

/** Every reading, newest first: where the points on the line come from, and a way to take one out. */
function Readings({ readings, isAdmin, onChange }: { readings: HsaBalance[]; isAdmin: boolean; onChange: () => void }) {
  const [asking, setAsking] = useState<string | null>(null);
  const remove = useAction(async (asOf: string) => {
    await api.delete(`/api/config/hsa-balances/${asOf}`);
    setAsking(null);
    toast('Reading removed');
    onChange();
  });
  const sorted = [...readings].sort((a, b) => b.as_of.localeCompare(a.as_of));
  return (
    <details className="more">
      <summary>Readings · {sorted.length}</summary>
      <div className="stack tight">
        <div className="list">
          {sorted.map((b) => {
            const s = splitBalance(b);
            return (
              <div className="item plain" key={b.as_of}>
                <span>
                  <div className="title">{formatDate(b.as_of, { year: 'always' })}</div>
                  <div className="sub">
                    {formatMoney(b.contributed_cents)} contributed · {formatMoney(s.growth)} growth
                    {b.withdrawn_cents > 0 ? ` · ${formatMoney(b.withdrawn_cents)} taken out` : ''}
                  </div>
                </span>
                <span className="end">
                  {formatMoney(b.balance_cents)}
                  {isAdmin ? (
                    asking === b.as_of ? (
                      <span className="actions end">
                        <button className="quiet muted" onClick={() => setAsking(null)} disabled={remove.pending}>
                          Keep
                        </button>
                        <button className="quiet danger" onClick={() => void remove.run(b.as_of)} disabled={remove.pending}>
                          Remove it
                        </button>
                      </span>
                    ) : (
                      <div>
                        <button className="quiet muted" onClick={() => setAsking(b.as_of)}>
                          Remove
                        </button>
                      </div>
                    )
                  ) : null}
                </span>
              </div>
            );
          })}
        </div>
        {remove.error ? <ErrorBox error={remove.error} /> : null}
      </div>
    </details>
  );
}

function BalanceForm({
  latest,
  custodian,
  onCancel,
  onSaved,
}: {
  latest: HsaBalance | null;
  custodian: string;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const today = localToday();
  const [asOf, setAsOf] = useState(today);
  const [balance, setBalance] = useState(centsToInput(latest?.balance_cents));
  const [contributed, setContributed] = useState(centsToInput(latest?.contributed_cents));
  const [withdrawn, setWithdrawn] = useState(latest ? centsToInput(latest.withdrawn_cents) : '0.00');

  const cents = { balance: inputToCents(balance), contributed: inputToCents(contributed), withdrawn: inputToCents(withdrawn) ?? 0 };
  const ready =
    /^\d{4}-\d{2}-\d{2}$/.test(asOf) &&
    asOf <= today &&
    cents.balance !== null &&
    cents.balance >= 0 &&
    cents.contributed !== null &&
    cents.contributed >= 0 &&
    cents.withdrawn >= 0;

  const save = useAction(async () => {
    await api.put(`/api/config/hsa-balances/${asOf}`, {
      balance_cents: cents.balance,
      contributed_cents: cents.contributed,
      withdrawn_cents: cents.withdrawn,
    });
    toast('Balance saved');
    onSaved();
  });

  return (
    <div className="stack tight">
      <p className="xs muted">
        {custodian.toLowerCase() === 'fidelity'
          ? 'On Fidelity NetBenefits, the balance and its date are on the HSA’s Summary tab. Contributions are on the Contributing tab, one total for each year: add them together.'
          : 'Copy these from your HSA’s website.'}
      </p>
      <div className="fields two">
        <div className="field">
          <label htmlFor="hsa-asof">As of</label>
          <input id="hsa-asof" type="date" max={today} value={asOf} onChange={(e) => setAsOf(e.target.value)} />
          <span className="hint">The date the site gives for the balance.</span>
        </div>
        <div className="field">
          <label htmlFor="hsa-balance">Balance</label>
          <input id="hsa-balance" inputMode="decimal" placeholder="0.00" value={balance} onChange={(e) => setBalance(e.target.value)} />
          <span className="hint">Cash and investments together.</span>
        </div>
        <div className="field">
          <label htmlFor="hsa-contributed">Contributed since it opened</label>
          <input
            id="hsa-contributed"
            inputMode="decimal"
            placeholder="0.00"
            value={contributed}
            onChange={(e) => setContributed(e.target.value)}
          />
          <span className="hint">Yours and your employer’s, every year added together.</span>
        </div>
        <div className="field">
          <label htmlFor="hsa-withdrawn">Taken out since it opened</label>
          <input id="hsa-withdrawn" inputMode="decimal" placeholder="0.00" value={withdrawn} onChange={(e) => setWithdrawn(e.target.value)} />
          <span className="hint">0.00 if nothing has been taken out.</span>
        </div>
      </div>
      {save.error ? <ErrorBox error={save.error} /> : null}
      <div className="actions">
        <button onClick={onCancel}>Cancel</button>
        <button className="primary" disabled={!ready || save.pending} onClick={() => void save.run()}>
          {save.pending ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}
